import React, { useState, useEffect } from 'react';
import { isSignInWithEmailLink } from 'firebase/auth';
import { auth } from './firebase/config';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AuthForm } from './components/AuthForm';
import { AuthSuccess } from './components/AuthSuccess';
import { WelcomeBack } from './components/WelcomeBack';
import { ChangeEmailPage } from './pages/ChangeEmailPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { DeleteAccountPage } from './pages/DeleteAccountPage';
import { EmailCallbackPage } from './pages/EmailCallbackPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { PostSignupPage } from './pages/PostSignupPage';
import { getAppName } from './utils/getAppName';
import { getInitialHref, getRedirectUrl, getIsExtension } from './utils/urlParams';
import './App.css';

// Main app component that handles routing based on URL params
const AppContent: React.FC = () => {
  const { user, loading, signOut } = useAuth();
  const [currentView, setCurrentView] = useState<'signIn' | 'redirect' | 'welcome-back' | 'dashboard' | 'changeEmail' | 'changePassword' | 'deleteAccount' | 'email-callback' | 'set-password' | 'post-signup' | undefined>();
  const [isResetFlow, setIsResetFlow] = useState(false);

  useEffect(() => {
    if (loading || currentView !== undefined) return;

    // Check for email link callback before anything else
    if (isSignInWithEmailLink(auth, getInitialHref())) {
      setCurrentView('email-callback');
      return;
    }

    if (user) {
      // Check if there's a redirect URL - if so, show welcome back first
      const redirectUrl = getRedirectUrl();
      const isExtension = getIsExtension();
      
      if (redirectUrl || isExtension) {
        setCurrentView('welcome-back');
      } else {
        setCurrentView('dashboard');
      }
    } else {
      setCurrentView('signIn');
    }
  }, [user, loading, currentView]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const redirectUrl = getRedirectUrl();
  const isExtension = getIsExtension();

  switch (currentView) {
    case 'welcome-back':
      return (
        <WelcomeBack
          onContinue={() => setCurrentView('redirect')}
          onUseDifferent={async () => {
            // Sign out the current user and go to auth form
            try {
              await signOut();
              setCurrentView('signIn');
            } catch (error) {
              console.error('Failed to sign out:', error);
              // Even if sign out fails, go to auth form
              setCurrentView('signIn');
            }
          }}
        />
      );
    
    case 'dashboard':
      return (
        <AuthSuccess 
          signOut={async () => {
            await signOut();
            setCurrentView('signIn');
          }}
          onChangeEmail={() => setCurrentView('changeEmail')}
          onChangePassword={() => setCurrentView('changePassword')}
          onDeleteAccount={() => setCurrentView('deleteAccount')}
        />
      );
    
    case 'changeEmail':
      return <ChangeEmailPage onBack={() => setCurrentView('dashboard')} />;
    
    case 'changePassword':
      return <ChangePasswordPage onBack={() => setCurrentView('dashboard')} />;
    
    case 'deleteAccount':
      return <DeleteAccountPage onBack={() => setCurrentView('dashboard')} onDeleted={() => setCurrentView('signIn')} />;
    
    case 'email-callback':
      return <EmailCallbackPage onComplete={(needsPassword, isNewUser) => {
        if (isNewUser) {
          // New user → show post-signup choice screen
          setCurrentView('post-signup');
        } else {
          // Existing user → go straight to redirect or dashboard
          setCurrentView((getRedirectUrl() || getIsExtension()) ? 'redirect' : 'dashboard');
        }
      }} />;

    case 'set-password':
      return <SetPasswordPage
        isReset={isResetFlow}
        onComplete={() => {
          setCurrentView((getRedirectUrl() || getIsExtension()) ? 'redirect' : 'dashboard');
        }}
      />;

    case 'post-signup': {
      const redirectUrl = getRedirectUrl();
      const isExtension = getIsExtension();
      const hasRedirect = !!(redirectUrl || isExtension);
      const destinationLabel = getAppName(redirectUrl || undefined);

      return <PostSignupPage
        destinationLabel={destinationLabel}
        onContinue={() => {
          setCurrentView(hasRedirect ? 'redirect' : 'dashboard');
        }}
        onSetPassword={() => {
          setIsResetFlow(false);
          setCurrentView('set-password');
        }}
      />;
    }

    case 'redirect':
      return <AuthSuccess redirectUrl={redirectUrl || undefined} isExtension={isExtension} />;

    case 'signIn':
    default:
      return <AuthForm onSuccess={() => setCurrentView((redirectUrl || isExtension) ? 'redirect' : 'dashboard')} />;
  }
};

function App() {
  return (
    <AuthProvider>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <AppContent />
      </div>
    </AuthProvider>
  );
}

export default App;
